import Joi from 'joi';

import { AppSubjectEnum } from '../../../../shared/modules/authz/enums/app-subject.enum';
import { pricingTierItemSchema } from '../../../../shared/modules/pricing-tier-items/validators/schemas/pricing-tier-item.schema';
import { DiscountConfigurationMode } from '../../../../shared/modules/vehicle-categories/enums/discount-configuration-mode.enum';
import { VehicleCategoryAvailabilityModeEnum } from '../../../../shared/modules/vehicle-categories/enums/vehicle-category-availability-mode.enum';
import { VehicleCategoryTypeEnum } from '../../../../shared/modules/vehicle-categories/enums/vehicle-category-type.enum';
import { vehicleCategoryFeatureItemSchema } from '../../../../shared/modules/vehicle-category-feature-items/validators/schemas/vehicle-category-feature-item.schema';
import { vehicleCategoryPricingItemPresetSchema } from '../../../../shared/modules/vehicle-category-pricing-item-presets/validators/schemas/vehicle-category-pricing-item-preset.schema';
import { createPoliciesAssignSchema } from '../../../policies/validators/schemas/policies-assign.schema';

/**
 * Schema for existing vehicle category record validation.
 */
export const updateVehicleCategorySchema = () =>
  Joi.object({
    id: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-category-id|cannot-be-null',
        }),
        otherwise: Joi.number().required().messages({
          'any.required': 'validations|vehicle-category-id|required',
          'number.base': 'validations|vehicle-category-id|must-be-number',
        }),
      })
      .prefs({ abortEarly: true }),

    name: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-category-name|cannot-be-null',
        }),
        otherwise: Joi.string().required().messages({
          'any.required': 'validations|vehicle-category-name|required',
          'string.base': 'validations|vehicle-category-name|must-be-string',
          'string.empty': 'validations|vehicle-category-name|cannot-be-empty',
        }),
      })
      .prefs({ abortEarly: true }),

    vehicleGroupId: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-vehicle-group-id|must-be-number',
    }),

    type: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|vehicle-category-type|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(VehicleCategoryTypeEnum))
          .required()
          .messages({
            'any.required': 'validations|vehicle-category-type|required',
            'any.only': 'validations|vehicle-category-type|invalid-value',
          }),
      })
      .prefs({ abortEarly: true }),

    description: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-category-description|must-be-string',
      'string.max': 'validations|vehicle-category-description|max-length',
    }),

    notes: Joi.string().allow(null, '').messages({
      'string.base': 'validations|vehicle-category-notes|must-be-string',
    }),

    availabilityMode: Joi.string()
      .valid(...Object.values(VehicleCategoryAvailabilityModeEnum))
      .optional()
      .empty(null)
      .messages({
        'any.only':
          'validations|vehicle-category-availability-mode|invalid-value',
      }),

    depositPercentage: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-category-deposit-percentage|must-be-number',
    }),

    depositFlatRate: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-category-deposit-flat-rate|must-be-number',
    }),

    standardDiscountPercentage: Joi.number()
      .min(0)
      .max(100)
      .allow(null)
      .messages({
        'number.base':
          'validations|vehicle-category-discount-percentage|must-be-number',
        'number.min': 'validations|vehicle-category-discount-percentage|min',
        'number.max': 'validations|vehicle-category-discount-percentage|max',
      }),

    discountConfigurationMode: Joi.string()
      .valid(...Object.values(DiscountConfigurationMode))
      .allow(null)
      .optional()
      .messages({
        'any.only':
          'validations|vehicle-category-discount-configuration-mode|invalid-value',
      }),

    excessAmount: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-category-excess-amount|must-be-number',
    }),

    reducedExcessAmount: Joi.number().allow(null).messages({
      'number.base':
        'validations|vehicle-category-reduced-excess-amount|must-be-number',
    }),

    minAgeLimit: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-min-age-limit|must-be-number',
    }),

    youngDriverAgeLimit: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-young-driver-age-limit|must-be-number',
    }),

    maxAgeLimit: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-max-age-limit|must-be-number',
    }),

    seniorAgeLimit: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-senior-age-limit|must-be-number',
    }),

    minDrivingYears: Joi.number().allow(null).integer().messages({
      'number.base':
        'validations|vehicle-category-min-driving-years|must-be-number',
    }),

    pricingTierItems: Joi.array().items(pricingTierItemSchema()).allow(null),
    featureItems: Joi.array()
      .items(vehicleCategoryFeatureItemSchema())
      .allow(null),
    pricingItemPresets: Joi.array()
      .items(vehicleCategoryPricingItemPresetSchema())
      .allow(null),

    policies: createPoliciesAssignSchema(AppSubjectEnum.VEHICLE_CATEGORY),

    files: Joi.any().allow(null),
  })
    // Ignore extra fields
    .unknown();
